<template>
  <Modal
    :active="active"
    :title="$t('forms.finance.title')"
    :vehicle="vehicle"
    class="financeCalculatorModal"
    :class="{ '-large': toolType === 'deautofinancier', 'modal-xl': toolType === 'bekaro' }"
    @modalClosed="closeModal"
    ref="modal"
  >
    <div class="form-modal-intro">
      <div v-if="introText" v-html="introText"></div>
    </div>

    <div v-if="WPSettings.LANGUAGE !== 'nl' && toolType !== 'standard'" class="infoMessage">
      {{ $t('forms.noTranslationAvailable') }}
    </div>

    <div
      v-if="toolType === 'edw'"
      id="edw-calculator"
      :data-dealer-id="leasetoolDealerId"
      :data-car-price="vehiclePrice"
      :data-car-license="license"
      :data-color-secondary="generalSettings.mainColor"
    ></div>

    <iframe
        v-if="active && toolType === 'financiallease'"
        ref="financialLeaseIframe"
        :key="financialLeaseWidgetKey"
        :srcdoc="financialLeaseWidgetHtml"
        :style="{ height: financialLeaseIframeHeight + 'px' }"
        class="financial-lease-widget"
        frameborder="0"
    ></iframe>

    <template v-if="toolType === 'rosfinance' && vehicle">
      <div id="ros-wrapper"></div>
      <script
        type="application/javascript"
        id="ros-finance-calculator"
        src="https://www.rosfinance.nl/clients/calculator.js"
        data-element-id="ros-wrapper"
        :data-api-key="rosfinanceAPIKey"
        :data-lease-amount="vehiclePrice"
        :data-construction-year="vehicle.properties.year"
        :data-business="false"
        :data-purchase-price="vehiclePrice"
        data-from-url="https://mekenkampautos.nl"
      ></script>
    </template>

    <template v-if="toolType === 'deautofinancier' && iframeUrl">
      <iframe :src="iframeUrl" width="100%" height="925" frameborder="0"></iframe>
    </template>

    <template v-if="toolType === 'bdlease' && bdleaseUrl">
      <iframe id="bdIframe" @load="onBdleaseFrameLoaded" :src="bdleaseUrl" width="100%" height="925" frameborder="0"></iframe>
    </template>

    <template v-if="toolType === 'bekaro' && bekaroUrl">
      <div class="form-modal-content">
        <iframe id="bekaro-calculator" :src="bekaroUrl" width="100%" height="100%" frameborder="0"></iframe>
      </div>
    </template>

    <div v-if="toolType === 'standard'">
      <div class="progressBar">
        <div
          class="progressBar__step -clickable"
          v-on:click="toStep(1, true)"
          :class="{ '-active': currentStep === 1, '-passed': currentStep > 1 }"
        >
          {{ $t('forms.finance.stepCalculator') }}
        </div>
        <div
          class="progressBar__step"
          v-on:click="toStep(2, true)"
          :class="{ '-active': currentStep === 2, '-passed': currentStep > 2 }"
        >
          {{ $t('forms.finance.stepPersonalDetails') }}
        </div>
        <div class="progressBar__step" :class="{ '-active': currentStep === 3, '-passed': currentStep > 3 }">
          {{ $t('forms.finance.stepConfirm') }}
        </div>
      </div>
      <!-- .privateLeaseModal__progress -->

      <form id="request-finance" @submit.prevent="toStep(4)">
        <div v-if="currentStep === 1" class="privateLeaseModal__page page-1">
          <div class="formGroup">
            <div class="formRow">
              <div class="formRow__field formRow__field--noWideLabel">
                <div class="form-radio form-radio--inline">
                  <input
                    id="calc-taxIncluded-true"
                    class="form-radio__input"
                    type="radio"
                    :value="false"
                    v-model="business"
                  />
                  <label class="form-radio__label" for="calc-taxIncluded-true">{{
                    $t('forms.finance.consumer')
                  }}</label>
                </div>
                <!-- .form-radio -->

                <div class="form-radio form-radio--inline">
                  <input
                    id="calc-taxIncluded-false"
                    class="form-radio__input"
                    type="radio"
                    :value="true"
                    v-model="business"
                  />
                  <label class="form-radio__label" for="calc-taxIncluded-false">{{
                    $t('forms.finance.business')
                  }}</label>
                </div>
                <!-- .form-radio -->
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup formGroup--transparent">
            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label"
                  >{{ $t('forms.finance.vehiclePrice') }}
                  <!-- <template v-if="business && vehicle.properties.btwMarge === 'BTW'">({{ $t('vehicle.exclTax') }})</template>
                  <template v-else>({{ $t('vehicle.inclTax') }})</template> -->
                </label>
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div class="formRow__field formRow__field--text formRow__field--large">
                {{ basePrice | toEUR }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup">
            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label">{{ $t('forms.finance.prepaymentTradeIn') }}</label>
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div class="formRow__field">
                <div class="rangeSlider rangeSlider--toEdge">
                  <veeno
                    v-model="selectedPrepayment"
                    :key="maxPrepayment"
                    :handles="[selectedPrepayment]"
                    :connect="[true, false]"
                    :range="{ min: 0, max: maxPrepayment }"
                    :step="10"
                  />
                </div>
                <!-- .rangeSlider -->
                <span class="rangeSlider__bottomValue">{{ selectedPrepayment | toEUR }}</span>
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup formGroup--transparent">
            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label">{{ $t('forms.finance.creditSumLoanAmount') }}</label>
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div class="formRow__field formRow__field--text formRow__field--large">
                {{ financeValue | toEUR }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup">
            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label">{{ $t('forms.finance.durationInMonths') }}</label>
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div class="formRow__field">
                <div class="rangeSlider rangeSlider--toEdge">
                  <veeno
                    v-model="selectedDuration"
                    :handles="[selectedDuration]"
                    :connect="[true, false]"
                    :range="{ min: 12, max: 60 }"
                    :step="12"
                  />
                </div>
                <!-- .rangeSlider -->
                <span class="rangeSlider__bottomValue">{{ parseInt(selectedDuration) }}</span>
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->

            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label">{{ $t('forms.finance.finalSum') }}</label>
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div
                class="formRow__field"
                :class="{ 'formRow__field--text formRow__field--disabled': maxFinalSum === 0 }"
              >
                <template v-if="maxFinalSum > 0">
                  <div class="rangeSlider rangeSlider--toEdge">
                    <veeno
                      :key="maxFinalSum"
                      v-model="selectedFinalSum"
                      :handles="[selectedFinalSum]"
                      :connect="[true, false]"
                      :range="{ min: 0, max: maxFinalSum }"
                      :step="10"
                    />
                  </div>
                  <!-- .rangeSlider -->
                  <span class="rangeSlider__bottomValue">{{ selectedFinalSum | toEUR }}</span>
                </template>
                <!-- if maxFinalSum > 0 -->
                <template v-else> {{ $t('forms.finance.finalSumError') }} {{ minFinanceValue | toEUR }}. </template>
                <!-- else -->
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup formGroup--transparent" v-if="getPackagesInLanguage.length > 0 && useDeliveryPackages">
            <div class="formRow__heading">
              <span class="formRow__heading__title">{{ $t('forms.buy.deliveryPackages') }}</span>
              <p class="formRow__heading__desc">{{ $t('forms.buy.deliveryPackagesDesc') }}</p>
            </div>
            <!-- .formRow__heading -->

            <div class="formRow">
              <div class="formRow__field formRow__field--text">
                <div v-for="pack in getPackagesInLanguage" :key="pack.delivery_name" class="form-radio">
                  <input
                    :id="pack.delivery_name"
                    type="radio"
                    class="form-radio__input"
                    :value="pack.delivery_name"
                    v-model="deliveryPackage"
                  />
                  <label :for="pack.delivery_name" class="form-radio__label">
                    <div class="option-list-extended">
                      <div class="option-list-extended__heading">
                        <h4>{{ pack.delivery_name }}</h4>
                        <div v-if="pack.delivery_price" class="option-list-extended__price">
                          {{ pack.delivery_price }}
                        </div>
                      </div>
                      <p v-if="pack.delivery_desc">
                        {{ pack.delivery_desc }}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <!-- .formGroup -->

          <div class="formGroup formGroup--transparent">
            <div class="formRow">
              <div class="formRow__label formRow__label--wide">
                <label class="form-label"
                  ><span class="resultText">{{ $t('forms.finance.monthlyFee') }}</span></label
                >
              </div>
              <!-- .formRow__label formRow__label--wide -->
              <div class="formRow__field formRow__field--text">
                <span class="resultText">{{ monthlyValue | toEUR }}</span>
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->

            <div class="formRow">
              <div class="formRow__field formRow__field--disabled">
                <p>{{ $t('forms.finance.calculationDisclaimer', { interestRate: interestRate }) }}</p>
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->
          <div style="display: flex">
            <button
              type="button"
              @click.prevent="toStep(2)"
              class="button button--primary button--arrow button--submit button--alignRight"
              style="margin-left: auto"
            >
              {{ $t('forms.privateLease.request') }}
            </button>
          </div>
        </div>
        <!-- .financeModal__page -->
        <div v-if="currentStep === 2" class="privateLeaseModal__page page-2">
          <p v-if="errors.length > 0" class="form-error">
            {{ $t('forms.errors.requiredFields') }}
          </p>

          <div class="formRow" v-if="!taxIncluded">
            <div class="formRow__label">
              <label class="form-label" for="leaseCalculator-companyName">{{ $t('forms.fields.companyName') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="leaseCalculator-companyName"
                class="form-textField"
                :class="{ 'form-textField--error': isError('companyName') }"
                type="text"
                v-model="companyName"
              />
              <p v-if="isError('companyName')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow" v-if="!taxIncluded">
            <div class="formRow__label">
              <label class="form-label" for="leaseCalculator-kvk">KVK</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input id="leaseCalculator-kvk" class="form-textField" type="text" v-model="kvk" />
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--radio">
              <div class="form-radio form-radio--inline">
                <input
                  id="lease-salutation-1"
                  class="form-radio__input"
                  type="radio"
                  value="Dhr."
                  v-model="salutation"
                />
                <label class="form-radio__label" for="lease-salutation-1">{{
                  $t('forms.fields.salutationMale')
                }}</label>
              </div>
              <!-- .form-radio -->

              <div class="form-radio form-radio--inline">
                <input
                  id="lease-salutation-2"
                  class="form-radio__input"
                  type="radio"
                  value="Mevr."
                  v-model="salutation"
                />
                <label class="form-radio__label" for="lease-salutation-2">{{
                  $t('forms.fields.salutationFemale')
                }}</label>
              </div>
              <!-- .form-radio -->

              <p v-if="isError('salutation')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="lease-fname">{{ $t('forms.fields.name') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <div class="formRow__field__grid">
                <div class="formRow__field__grid__column">
                  <input
                    id="lease-fname"
                    class="form-textField"
                    :class="{ 'form-textField--error': isError('fname') }"
                    type="text"
                    v-model="fname"
                    :placeholder="$t('forms.fields.firstName')"
                  />
                  <p v-if="isError('fname')" class="form-fieldError">
                    {{ $t('forms.errors.requiredFieldSingle') }}
                  </p>
                </div>
                <!-- .formRow__field__grid__column -->
                <div class="formRow__field__grid__column">
                  <input
                    id="lease-lname"
                    class="form-textField"
                    :class="{ 'form-textField--error': isError('lname') }"
                    type="text"
                    v-model="lname"
                    :placeholder="$t('forms.fields.lastName')"
                  />
                  <p v-if="isError('lname')" class="form-fieldError">
                    {{ $t('forms.errors.requiredFieldSingle') }}
                  </p>
                </div>
                <!-- .formRow__field__grid__column -->
              </div>
              <!-- .formRow__field__grid -->
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="leaseCalculator-email">{{ $t('forms.fields.email') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="leaseCalculator-email"
                class="form-textField"
                :class="{ 'form-textField--error': isError('email') }"
                type="email"
                v-model="email"
              />
              <p v-if="isError('email')" class="form-fieldError">
                {{ $t('forms.errors.validEmail') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="lease-zip">{{ $t('forms.fields.zip') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="lease-zip"
                class="form-textField"
                :class="{ 'form-textField--error': isError('zip') }"
                type="text"
                v-model="zip"
                @change="fetchAddress()"
              />
              <p v-if="isError('zip')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="lease-houseNumber">{{ $t('forms.fields.houseNumber') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <div class="formRow__field__grid">
                <input
                  id="leaseCalculator-houseNumber"
                  class="form-textField formRow__field__grid__column"
                  :class="{ 'form-textField--error': isError('houseNumber') }"
                  type="text"
                  @change="fetchAddress()"
                  v-model="houseNumber"
                />

                <input
                  id="leaseCalculator-houseNumberSupplement"
                  class="form-textField formRow__field__grid__column"
                  :class="{
                    'form-textField--error': isError('houseNumberSupplement'),
                  }"
                  type="text"
                  :placeholder="$t('forms.fields.houseNumberSupplement')"
                  v-model="houseNumberSupplement"
                />
              </div>
              <!-- .formRow__field__grid -->
              <p v-if="isError('houseNumber')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="addressError && !manualAddress" class="formRow">
            <div class="formRow__field formRow__field--noLabel">
              <p class="form-fieldWarning form-fieldWarning--noMargin">
                {{ $t('forms.errors.noAddressFound.text') }}
                <button type="button" class="button button--anchor" @click.prevent="fillManuallAddress()">
                  {{ $t('forms.errors.noAddressFound.button') }}
                </button>
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="street || manualAddress" class="formRow" :class="{ 'formRow--compact': !manualAddress }">
            <div class="formRow__label">
              <label class="form-label" for="lease-street">{{ $t('forms.fields.street') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
              <span v-if="!manualAddress">{{ street }}</span>
              <input
                v-if="manualAddress"
                id="lease-street"
                class="form-textField"
                :class="{ 'form-textField--error': isError('street') }"
                type="text"
                v-model="street"
              />
              <p v-if="isError('street')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="city || manualAddress" class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="lease-city">{{ $t('forms.fields.city') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
              <span v-if="!manualAddress">{{ city }}</span>
              <input
                v-if="manualAddress"
                id="lease-city"
                class="form-textField"
                :class="{ 'form-textField--error': isError('city') }"
                type="text"
                v-model="city"
              />
              <p v-if="isError('city')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="leaseCalculator-houseNumber">{{ $t('forms.fields.phone') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="leaseCalculator-phone"
                class="form-textField"
                :class="{ 'form-textField--error': isError('phone') }"
                type="text"
                v-model="phone"
              />
              <p v-if="isError('phone')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow formRow--spaced">
            <div class="formRow__field formRow__field--noLabel">
              <div class="form-checkbox">
                <input id="lease-acceptTerms" class="form-checkbox__input" type="checkbox" v-model="acceptTerms" />
                <label class="form-checkbox__label" for="lease-acceptTerms">
                  <span
                    >{{ $t('forms.fields.acceptTermsPre') }}
                    <a :href="termsUrl" target="_blank">{{ $t('forms.fields.terms') }}</a></span
                  >
                </label>
                <p v-if="isError('acceptTerms')" class="form-fieldError form-fieldError--spaced">
                  {{ $t('forms.errors.acceptTerms') }}
                </p>
              </div>
              <!-- .form-checkbox -->
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div style="display: flex">
            <button
              type="button"
              @click.prevent="toStep(3)"
              class="button button--primary button--arrow button--submit button--alignRight"
              style="margin-left: auto"
            >
              {{ $t('forms.privateLease.toConfirmStep') }}
            </button>
          </div>
        </div>
        <!-- .financeModal__page -->

        <div v-if="currentStep === 3" class="privateLeaseModal__page page-3">
          <p v-if="errorMsg" class="form-error">{{ errorMsg }}</p>

          <p class="privateLeaseModal__page__description">
            {{ $t('forms.finance.confirmDesc') }}
          </p>

          <div class="formGroup formGroup--pushBottom">
            <div class="formRow__heading">
              <span class="formRow__heading__title">{{ $t('forms.finance.financeDetails') }}</span>
              <button class="button button--anchor" v-on:click="toStep(1)">
                ({{ $t('forms.privateLease.edit') }})
              </button>
            </div>
            <!-- .formRow__heading -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.privateLease.prepayment') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ selectedPrepayment | toEUR }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.finance.durationInMonths') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">{{ parseInt(selectedDuration) }}</div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact" v-if="getPackagesInLanguage.length > 0 && useDeliveryPackages">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.buy.deliveryPackages') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ deliveryPackagePrice | toEUR }} ({{ deliveryPackage }})
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.finance.finalSum') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ selectedFinalSum | toEUR }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.finance.monthlyFee') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ monthlyValue | toEUR }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->

          <div class="formGroup formGroup--pushBottom">
            <div class="formRow__heading">
              <span class="formRow__heading__title">{{ $t('forms.privateLease.personalDetails') }}</span>
              <button type="button" class="button button--anchor" @click.prevent="toStep(2)">
                ({{ $t('forms.privateLease.edit') }})
              </button>
            </div>
            <!-- .formRow__heading -->

            <div v-if="!taxIncluded" class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.fields.companyName') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ companyName }} <span v-if="kvk">(KVK: {{ kvk }})</span>
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.fields.name') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ salutation === 'Dhr.' ? $t('forms.fields.salutationMale') : $t('forms.fields.salutationFemale') }}
                {{ fname }}
                {{ lname }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.fields.email') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ email }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.fields.address') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ street }} {{ houseNumber }}{{ houseNumberSupplement ? ' ' + houseNumberSupplement : '' }}, {{ zip }},
                {{ city }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
            <div class="formRow formRow--compact">
              <div class="formRow__label">
                <span class="form-label">{{ $t('forms.fields.phone') }}</span>
              </div>
              <!-- .formRow__label -->
              <div class="formRow__field formRow__field--text">
                {{ phone }}
              </div>
              <!-- .formRow__field -->
            </div>
            <!-- .formRow -->
          </div>
          <!-- .formGroup -->
          <div style="display: flex">
            <button
              type="submit"
              class="button button--primary button--arrow button--submit"
              :class="{ 'button--loading': loading }"
              :disabled="loading"
              style="margin-left: auto"
            >
              {{ $t('forms.fields.submitRequest') }}
            </button>
          </div>
        </div>
        <!-- .financeModal__page -->
        <div v-if="currentStep === 4" class="privateLeaseModal__page page-4">
          <p v-if="successMsg" class="form-success">{{ successMsg }}</p>
        </div>
        <!-- .financeModal__page -->
      </form>

      <img class="financeCalculatorModal__banner" :src="WPSettings.ASSETS_DIR + '/banner-afm.jpg'" />
    </div>
    <!-- .v-else -->
  </Modal>
</template>

<style lang="scss">
.imot.financeCalculatorModal {
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: center;

  .financeCalculatorModal {
    &__banner {
      display: block;
      width: 100%;
      height: auto;
      margin-top: 15px;
    }
  }

  .form-modal {
    margin-top: 5vh;
    position: relative;
    transform: none;
    top: 0;
    left: 0;
  }

  #edw-calculator {
    border: 1px solid #ccc;

    img {
      display: none;
    }

    button {
      margin-top: 1em;
      background: rgb(235, 169, 39);
      border-radius: 20px;
      padding: 12px 25px;
    }

    input[type='number'] {
      margin: 5px 0;
      border-radius: 6px;
      padding: 10px;
    }

    #edw-checkbox-privacy-agreement {
      display: none;

      & + label {
        min-height: 20px;
      }
    }

    .edw-calculator__container {
      & > div > div:nth-child(2) > div:nth-child(3) {
        flex-direction: column;
      }
    }
  }

  .resultText {
    font-size: 20px;
    font-weight: bold;
    color: #000;
  }

  .form-label {
    color: #000;
  }
}
</style>

<script>
import veeno from 'veeno' // eslint-disable-line
import Modal from '@/components/Modal'
import { WPSettings } from '@/WPSettings'
import axios from 'axios'

export default {
  name: 'modalCalculator',
  data() {
    return {
      WPSettings: WPSettings,
      generalSettings: this.$store.state.settings.general,
      loading: false,
      errors: [],
      errorMsg: '',
      successMsg: '',
      currentStep: 1,

      // Address fetch
      debounceTimer: null,
      addressError: false,
      manualAddress: false,

      // Form
      business: false,
      taxIncluded: true,
      selectedPrepayment: 0,
      selectedDuration: 60,
      selectedFinalSum: 0,
      deliveryPackage: '',
      salutation: '',
      fname: '',
      lname: '',
      email: '',
      zip: '',
      street: '',
      houseNumber: '',
      houseNumberSupplement: '',
      city: '',
      phone: '',
      companyName: '',
      kvk: '',

      acceptTerms: false,
      termsUrl: this.$store.state.settings.general.termsUrl,

      externalScript: null,
      financialLeaseIframeHeight: 500,
      financialLeaseResizeTimeout: null,
      random: null,
      iFrameResizerLoaded: false
    }
  },
    mounted() {
        window.addEventListener(
            'message',
            this.handleFinancialLeaseMessage
        )
        this.loadIframeResizer().then(() => {
            this.iFrameResizerLoaded = true;
        });
    },

    beforeDestroy() {
        window.removeEventListener(
            'message',
            this.handleFinancialLeaseMessage
        )
        clearTimeout(this.financialLeaseResizeTimeout)
    },
  computed: {
    getPackagesInLanguage() {
      const output = []

      this.packages.forEach((item) => {
        const pack = this.createDeliveryPackage(item)
        output.push(pack)
      })
      if (output.length > 0) {
        this.deliveryPackage = output[0]['delivery_name']
      }
      return output
    },
    deliveryPackagePrice() {
      const dPackage = this.packages.find((p) => p.delivery_name === this.deliveryPackage)
      if (!dPackage) return 0
      return parseInt(dPackage.delivery_price)
    },
    currentVehicleId() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle ? currentVehicle.id : null
    },
    vehiclePrice() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.price
    },
    basePriceExclTax() {
      const currentVehicle = this.$store.getters['vehicles/current']
      const price = currentVehicle.properties.price

      return this.business ? price / 1.21 : price
    },
    basePrice() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.btwMarge === 'BTW' && this.business
        ? this.basePriceExclTax
        : currentVehicle.properties.price
    },
    minFinanceValue() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.btwMarge === 'BTW' && this.business
        ? this.minFinanceValueExclTax
        : this.minFinanceValueExclTax * 1.21
    },
    financeValue() {
      return this.basePrice - parseFloat(this.selectedPrepayment)
    },
    maxFinalSum() {
      return Math.max(0, this.financeValue - this.minFinanceValue)
    },
    maxPrepayment() {
      return this.basePrice - this.minFinanceValue
    },
    monthlyValue() {
      const finalterm = parseFloat(this.selectedFinalSum) // fv
      const amount = -this.financeValue - this.deliveryPackagePrice // pv
      const rate = (this.interestRate / 100 + 1) ** (1 / 12) - 1
      const type = 0
      const duration = parseFloat(this.selectedDuration)
      const rateDuration = (1 + rate) ** duration
      return (-finalterm - amount * rateDuration) / (1 + rate * type) / ((rateDuration - 1) / rate)
    },
    license() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.license
    },
    bekaroUrl() {
      if (this.toolType !== 'bekaro') {
        return
      }
      
      const currentVehicle = this.$store.getters['vehicles/current']
      if (! currentVehicle.properties.price) {
        return
      }

      const priceHasTax = currentVehicle.properties.priceInclTax

      const baseUrl = 'https://bereken.bekarolease.nl/calculator/' + this.bekaroUniqueKey + '/'
      const principal = '?principal=' + (priceHasTax ? currentVehicle.properties.price : Math.ceil(currentVehicle.properties.price * 1.21))
      const taxOrMarge = '&tax-or-marge=' + (currentVehicle.properties.btwMarge === 'BTW' ? 'btw' : 'marge')
      const principalNoTax = '&principal-no-tax=' + (priceHasTax ? Math.ceil(currentVehicle.properties.price / 1.21) : currentVehicle.properties.price)
      const license = currentVehicle.properties.license ? '&license=' + currentVehicle.properties.license : ''
      const url = '&url=' + window.location.href

      return baseUrl + principal + taxOrMarge + principalNoTax + license + url + '&duration=72'
    },
    financialLeaseProduct() {
      if (this.toolType !== 'financiallease') {
        return
      }
      
      const currentVehicle = this.$store.getters['vehicles/current']
      if (! currentVehicle.properties.price) {
        return
      }

      const priceHasTax = currentVehicle.properties.priceInclTax
      const vat = priceHasTax ? 
        currentVehicle.properties.price - (currentVehicle.properties.price / 1.21) : 
        currentVehicle.properties.price * 0.21

        const product = {
            salesPriceExclVat: priceHasTax ? currentVehicle.properties.price - vat : currentVehicle.properties.price,
            vat,
            marginScheme: 'Vat',
            vehicleType: 'Personal',
            brand: currentVehicle.properties.brand,
            model: currentVehicle.properties.model,
            licensePlate: currentVehicle.properties.license,
            ageInMonths: currentVehicle.properties.ageInMonths,
        }

        return JSON.stringify(product)
    },
    financialLeaseWidgetKey() {
        const currentVehicle = this.$store.getters['vehicles/current']
        return currentVehicle.id
    },
    financialLeaseWidgetHtml() {
        const productDetails =
            typeof this.financialLeaseProduct === 'string'
                ? this.financialLeaseProduct
                : JSON.stringify(this.financialLeaseProduct)

        const dealerContext = JSON.stringify({
            id: this.financialLeaseDealerId
        })

        return `
            <!doctype html>
            <html>
                <head>
                    <meta charset="utf-8">

                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1"
                    >

                    <style>
                        html,
                        body {
                            margin: 0;
                            padding: 0;
                            width: 100%;
                            overflow: hidden;
                        }

                        #financial-lease-calculator {
                            width: 100%;
                        }
                    </style>
                </head>

                <body>
                    <div id="financial-lease-calculator"></div>

                    <script>
                        function updateHeight() {
                            const height = Math.max(
                                document.body.scrollHeight,
                                document.documentElement.scrollHeight,
                                document.body.offsetHeight,
                                document.documentElement.offsetHeight
                            )

                            window.parent.postMessage({
                                type: 'financial-lease-height',
                                height: height
                            }, '*')
                        }

                        const resizeObserver = new ResizeObserver(() => {
                            updateHeight()
                        })

                        resizeObserver.observe(document.body)

                        const mutationObserver = new MutationObserver(() => {
                            updateHeight()
                        })

                        mutationObserver.observe(document.body, {
                            childList: true,
                            subtree: true,
                            attributes: true
                        })

                        window.addEventListener('load', updateHeight)

                        setTimeout(updateHeight, 100)
                        setTimeout(updateHeight, 500)
                        setTimeout(updateHeight, 1000)
                    <\/script>

                    <script
                        id="fl:dealer-widget"
                        type="module"
                        crossorigin="anonymous"
                        data-widget="calculator"
                        data-target="#financial-lease-calculator"
                        data-dealer-source="FinancialLeasePartner"
                        data-dealer-context="${this.escapeHtml(dealerContext)}"
                        data-custom-primary="${this.generalSettings.mainColor}"
                        data-custom-accent="${this.generalSettings.mainColor}"
                        data-product-details="${this.escapeHtml(productDetails)}"
                        src="https://embed.financiallease.nl/widget.js"
                    ><\/script>
                </body>
            </html>
        `
    },

    bdleaseUrl() {
        if (this.toolType !== 'bdlease') {
            return
        }

        const currentVehicle = this.$store.getters['vehicles/current']
        if (!currentVehicle.properties.price) {
            return
        }

        const baseUrl = `https://brokerdash.nl/embed/${this.bdlease.dealerId}/nfl-calculator-tool`
        const view = '?view=' + this.bdlease.defaultTab
        const price = '&price=' + currentVehicle.properties.price
        const isBtw = '&vehicle_vat=' + (currentVehicle.properties.btwMarge === 'BTW')
        const vatInEx = '&vat=' + (currentVehicle.properties.priceInclTax ? 'in' : 'ex')
        const deposit = '&deposit=' + this.bdlease.defaultDeposit
        const closingTerm = '&closing_term=' + this.bdlease.defaultClosingTerm
        const licensePlate = currentVehicle.properties.license ? '&license_plate=' + currentVehicle.properties.license : ''
        const brand = '&brand=' + currentVehicle.properties.brand
        const model = '&model=' + currentVehicle.properties.model
        const constructionYear = '&construction_year=' + currentVehicle.properties.year
        const url = baseUrl + view + price + isBtw + vatInEx + deposit + closingTerm + licensePlate + brand + model + constructionYear
        
        return url
    },

    iframeUrl() {
      if (this.toolType !== 'deautofinancier') {
        return
      }

      const currentVehicle = this.$store.getters['vehicles/current']
      const baseUrl = 'https://calc.deautofinancier.nl/?calc=ck&design=' + this.deautofinancierDealerId
      const purchPrice = '&purch_price=' + currentVehicle.properties.price
      const vatMargin = '&vat_margin=' + (currentVehicle.properties.btwMarge === 'BTW' ? 'v' : 'm')
      const vatInEx = '&vat_inex=' + (currentVehicle.properties.priceInclTax ? 'in' : 'ex')

      return baseUrl + purchPrice + vatMargin + vatInEx
    },
  },
  props: [
    'active',
    'toolType',
    'minFinanceValueExclTax',
    'interestRate',
    'leasetoolDealerId',
    'rosfinanceAPIKey',
    'deautofinancierDealerId',
    'bekaroUniqueKey',
    'financialLeaseDealerId',
    'bdlease',
    'introText',
    'vehicle',
    'packages',
    'useDeliveryPackages',
  ],
  components: {
    Modal,
    veeno,
  },
  watch: {
    business(val) {
        this.taxIncluded = !val
    },
    active(isModalActive) {        
        if (!this.externalScript) {
            const externalScript = document.createElement('script')
            externalScript.setAttribute('src', 'https://calculator.edwautolease.nl/widget/1.0/bundle.min.js')
            this.externalScript = externalScript
        }

        if (isModalActive && this.toolType === 'edw') {
            this.$nextTick(function () {
                document.body.appendChild(this.externalScript)
            })
        } else if (this.toolType === 'edw') {
            this.$nextTick(function () {
                document.body.removeChild(this.externalScript)
                this.externalScript = null
            })
        }
    },
  },
  methods: {
    loadIframeResizer() {
        return new Promise((resolve, reject) => {
            // Script al geladen?
            if (window.iFrameResize) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.2.11/iframeResizer.min.js';
            script.async = true;

            script.onload = () => resolve();
            script.onerror = () => reject(
                new Error('Script kon niet geladen worden')
            );

            document.body.appendChild(script);
        });
    },
    onBdleaseFrameLoaded() {
        if (!this.iFrameResizerLoaded) {
            return '';
        }
        window.iFrameResize({ inPageLinks: true }, '#bdIframe')
    },

    handleFinancialLeaseMessage(event) {
        const iframe = this.$refs.financialLeaseIframe

        if (!iframe) {
            return
        }

        if (event.source !== iframe.contentWindow) {
            return
        }

        if (
            !event.data ||
            event.data.type !== 'financial-lease-height'
        ) {
            return
        }

        const height = Number(event.data.height)

        if (!height || height < 100) {
            return
        }

        if (height === this.financialLeaseIframeHeight) {
            return
        }

        // Hoogte direct aanpassen
        this.financialLeaseIframeHeight = height

        // Vorige debounce annuleren
        clearTimeout(this.financialLeaseResizeTimeout)

        // Pas scrollen als de widget even niet meer van hoogte verandert
        this.financialLeaseResizeTimeout = setTimeout(() => {
            this.$nextTick(() => {
                iframe.scrollIntoView({
                    behavior: 'instant',
                    block: 'start'
                })
            })
        }, 150)
    },
    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
    },
    createDeliveryPackage(item) {
      const output = {
        delivery_price: item.delivery_price,
        delivery_name: item.delivery_name,
        delivery_desc: item.delivery_desc,
      }
      if (item[`delivery_name_${WPSettings.LANGUAGE}`]) {
        output.delivery_name = item[`delivery_name_${WPSettings.LANGUAGE}`]
      }
      if (item[`delivery_desc_${WPSettings.LANGUAGE}`]) {
        output.delivery_desc = item[`delivery_desc_${WPSettings.LANGUAGE}`]
      }
      return output
    },
    setDefaultDeposit() {
      return (this.selectedPrepayment =
        (this.$store.state.settings.modules.financeCalculator.defaultDeposit / 100) * this.basePrice)
    },
    toStep(step, onlyBack = false) {
      if (step > this.currentStep) {
        if (onlyBack) {
          return
        }

        if (step > 2) {
          this.validateForm()

          if (this.errors.length) {
            this.currentStep = 2
            return
          }
        }

        if (step > 3) {
          this.submitForm()
          return
        }
      }
      this.errorMsg = ''
      this.successMsg = ''
      this.currentStep = step
    },
    validateForm() {
      // Reset errors
      this.errors = []

      // Fields to check
      const checkIfHasValue = [
        'salutation',
        'fname',
        'lname',
        'email',
        'zip',
        'street',
        'houseNumber',
        'city',
        'phone',
        'acceptTerms',
        'companyName',
      ]

      // Check fields
      checkIfHasValue.forEach((field) => {
        if (field == 'companyName') {
          if (!this.taxIncluded) {
            if (!this.companyName) {
              this.errors.push('companyName')
            }
          }
        } else {
          if (!this[field]) {
            // Field has no value, so add it to error list
            this.errors.push(field)
          } else if (field === 'email') {
            // Test email formatting
            const regexp =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

            if (!regexp.test(this[field].toLowerCase())) {
              this.errors.push(field)
            }
          }
        }
      })
    },
    isError(field) {
      // Check if field is in the error list
      return this.errors.includes(field)
    },
    closeModal() {
      // Emit to parent component
      this.$emit('modalClosed', true)

      // Reset errors - just because it's prettier
      this.errors = []
    },
    fetchAddress() {
      // Format field values
      if (this.zip) this.zip = this.zip.replace(' ', '').toUpperCase()
      if (this.houseNumber) {
        const convertHouseNumber = parseInt(this.houseNumber)
        this.houseNumber = !isNaN(convertHouseNumber) ? convertHouseNumber : ''
      }

      // Check if values exist
      if (!this.zip || !this.houseNumber) {
        return
      }

      // Clear current running timer
      clearTimeout(this.debounceTimer)

      // Start a new debounce timer
      this.debounceTimer = setTimeout(() => {
        axios
          .get(
            WPSettings.API_URL + '/get-address',
            {
              params: {
                number: this.houseNumber,
                zip: this.zip,
              },
            },
            {
              headers:
                process.env.NODE_ENV === 'production'
                  ? {
                      'X-WP-Nonce': WPSettings.NONCE,
                    }
                  : {},
            }
          )
          .then((response) => {
            this.city = response.data.city
            this.street = response.data.street
            this.manualAddress = false
            this.addressError = false

            // Remove from error list
            this.errors = this.errors.filter((item) => item !== 'city' && item !== 'street')
          })
          .catch(() => {
            this.addressError = true
            this.city = this.street = ''
          })
      }, 500)
    },
    fillManuallAddress() {
      this.manualAddress = true
    },
    submitForm() {
      // Do the call to WP REST
      this.loading = true
      const form = {
        basePrice: this.basePrice,
        toFinance: this.financeValue,
        acceptTerms: this.acceptTerms ? true : null,
        vehicleId: this.currentVehicleId,
        duration: this.selectedDuration,
        finalSum: this.selectedFinalSum,
        prepayment: this.selectedPrepayment,
        deliveryPackage: this.deliveryPackage,
        deliveryPackagePrice: this.deliveryPackagePrice,
        monthlyFee: this.monthlyValue,
        taxIncluded: this.taxIncluded,
        salutation: this.salutation,
        fname: this.fname,
        lname: this.lname,
        email: this.email,
        phoneNumber: this.phone,
        street: this.street,
        houseNumber: this.houseNumber,
        houseNumberSupplement: this.houseNumberSupplement,
        zip: this.zip,
        city: this.city,
        companyName: this.companyName,
        kvk: this.kvk,
      }
      axios
        .post(WPSettings.API_URL + '/form-finance', form, {
          headers:
            process.env.NODE_ENV === 'production'
              ? {
                  'X-WP-Nonce': WPSettings.NONCE,
                }
              : {},
        })
        .then((response) => {
          if (response.data.status === 'success') {
            // Update success message
            this.errorMsg = ''
            this.successMsg = response.data.message
            this.currentStep = 4

            // Reset form
            this.salutation =
              this.fname =
              this.lname =
              this.email =
              this.phone =
              this.street =
              this.houseNumber =
              this.houseNumberSupplement =
              this.zip =
              this.city =
              this.companyName =
              this.kvk =
              this.acceptTerms =
                ''
          }
        })
        .catch((error) => {
          if (error.response.data.status === 'error') {
            // Update error message
            this.successMsg = ''
            this.errorMsg = error.response.data.message
          } else {
            this.successMsg = ''
            this.errorMsg = this.$t('forms.errors.somethingWentWrong')
          }
        })
        .finally(() => (this.loading = false))
    },
  },
  created() {
    this.setDefaultDeposit()
  },
}
</script>

<style>
    .financial-lease-widget {
        display: block;
        width: 100%;
        border: 0;
        transition: height 0.2s ease;
    }
</style>